import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Building2,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Plus,
  RefreshCw,
  Search,
  Users,
  X,
} from 'lucide-react';
import PlatformHeader from '../../components/platform-admin/PlatformHeader';
import {
  createPlatformCompany,
  getPlatformCompanies,
  updatePlatformCompany,
} from '../../utils/platformCompanyApi';
import { ApiValidationError } from '../../types/auth';
import type {
  CreatePlatformCompanyData,
  PlatformCompany,
} from '../../types/platformAdmin';
import type { PaginationMeta } from '../../types/journalEntry';
import { toast, toastApiError } from '../../utils/toast';

const PAGE_SIZE_OPTIONS = [10, 15, 25, 50] as const;
const SEARCH_DEBOUNCE_MS = 400;

type SortOption = 'name' | '-name' | 'created_at' | '-created_at';

const defaultCreateForm = (): CreatePlatformCompanyData => ({
  name: '',
  email: '',
  phone: '',
  address: '',
  tax_number: '',
  currency: 'LKR',
  active: true,
  admin: {
    name: '',
    email: '',
    password: '',
    active: true,
  },
});

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

const Dashboard = () => {
  const [companies, setCompanies] = useState<PlatformCompany[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sort, setSort] = useState<SortOption>('name');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState<CreatePlatformCompanyData>(defaultCreateForm());
  const [createError, setCreateError] = useState<string | null>(null);
  const [createFieldErrors, setCreateFieldErrors] = useState<Record<string, string[] | undefined>>({});
  const [creating, setCreating] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchCompanies = useCallback(async (targetPage = page) => {
    setLoading(true);
    setError(null);

    try {
      const result = await getPlatformCompanies({
        search: searchQuery || undefined,
        active:
          statusFilter === 'all' ? undefined : statusFilter === 'active',
        page: targetPage,
        per_page: perPage,
        sort,
      });
      setCompanies(result.data);
      setMeta(result.meta);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, searchQuery, sort, statusFilter]);

  useEffect(() => {
    fetchCompanies(page);
  }, [fetchCompanies, page]);

  const summary = useMemo(() => {
    const activeCount = companies.filter((c) => c.active).length;
    const inactiveCount = companies.filter((c) => !c.active).length;
    const totalUsers = companies.reduce((sum, c) => sum + c.users_count, 0);
    return { activeCount, inactiveCount, totalUsers };
  }, [companies]);

  const openCreateModal = () => {
    setCreateForm(defaultCreateForm());
    setCreateError(null);
    setCreateFieldErrors({});
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setCreateError(null);
    setCreateFieldErrors({});
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateError(null);
    setCreateFieldErrors({});

    try {
      const payload: CreatePlatformCompanyData = {
        name: createForm.name.trim(),
        currency: createForm.currency || 'LKR',
        active: createForm.active ?? true,
        admin: {
          name: createForm.admin.name.trim(),
          email: createForm.admin.email.trim(),
          password: createForm.admin.password,
          active: createForm.admin.active ?? true,
        },
      };

      if (createForm.email?.trim()) payload.email = createForm.email.trim();
      if (createForm.phone?.trim()) payload.phone = createForm.phone.trim();
      if (createForm.address?.trim()) payload.address = createForm.address.trim();
      if (createForm.tax_number?.trim()) payload.tax_number = createForm.tax_number.trim();

      const result = await createPlatformCompany(payload);
      closeCreateModal();
      toast.success(
        `Created "${result.data.company.name}" with admin ${result.data.admin.email}.`
      );
      setPage(1);
      fetchCompanies(1);
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setCreateFieldErrors(err.errors);
        const firstFieldError = Object.values(err.errors).flat()[0];
        setCreateError(firstFieldError || err.message);
      } else {
        toastApiError(err, 'Failed to create company');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleToggleActive = async (company: PlatformCompany) => {
    setTogglingId(company.id);
    try {
      await updatePlatformCompany(company.id, { active: !company.active });
      toast.success(
        `${company.name} is now ${company.active ? 'inactive' : 'active'}.`
      );
      fetchCompanies(page);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update company');
    } finally {
      setTogglingId(null);
    }
  };

  const createFieldError = (field: string) => createFieldErrors[field]?.[0];

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <PlatformHeader />

      <main className="flex-1 p-4 lg:p-6">
        <div className="max-w-7xl mx-auto flex flex-col gap-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Companies</h1>
              <p className="text-sm text-slate-500 mt-1">
                Provision and manage tenant companies on the platform
              </p>
            </div>
            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-700 hover:bg-indigo-600 transition-colors"
            >
              <Plus className="h-4 w-4" />
              New Company
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-indigo-700" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Total (this page)</p>
                  <p className="text-2xl font-bold text-slate-900">{meta?.total ?? companies.length}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Active on page</p>
                  <p className="text-2xl font-bold text-slate-900">{summary.activeCount}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Users on page</p>
                  <p className="text-2xl font-bold text-slate-900">{summary.totalUsers}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label htmlFor="company-search" className="block text-sm font-medium text-slate-700 mb-1">
                    Search
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <input
                      id="company-search"
                      type="text"
                      placeholder="Name, email, or tax number..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-indigo-600 focus:border-indigo-600"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="statusFilter" className="block text-sm font-medium text-slate-700 mb-1">
                    Status
                  </label>
                  <select
                    id="statusFilter"
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value as 'all' | 'active' | 'inactive');
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                  >
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="sortFilter" className="block text-sm font-medium text-slate-700 mb-1">
                    Sort
                  </label>
                  <select
                    id="sortFilter"
                    value={sort}
                    onChange={(e) => {
                      setSort(e.target.value as SortOption);
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                  >
                    <option value="name">Name (A–Z)</option>
                    <option value="-name">Name (Z–A)</option>
                    <option value="created_at">Oldest first</option>
                    <option value="-created_at">Newest first</option>
                  </select>
                </div>
              </div>
            </div>

            {error && (
              <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-100 rounded-lg flex items-start gap-2 text-sm">
                <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
                <p className="text-red-800 flex-1">{error}</p>
                <button type="button" onClick={() => fetchCompanies(page)} className="text-red-700 underline">
                  Retry
                </button>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Currency
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Users
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-sm text-slate-500">
                        <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2 text-indigo-600" />
                        Loading companies...
                      </td>
                    </tr>
                  ) : companies.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-sm text-slate-500">
                        No companies found.
                      </td>
                    </tr>
                  ) : (
                    companies.map((company) => (
                      <tr key={company.id} className="hover:bg-slate-50/80">
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-900">{company.name}</div>
                          {company.tax_number && (
                            <div className="text-xs text-slate-500 mt-0.5">Tax: {company.tax_number}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          <div>{company.email || '—'}</div>
                          {company.phone && <div className="text-xs text-slate-400">{company.phone}</div>}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{company.currency}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{company.users_count}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{formatDate(company.created_at)}</td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              company.active
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            {company.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            type="button"
                            disabled={togglingId === company.id}
                            onClick={() => handleToggleActive(company)}
                            className="text-sm font-medium text-indigo-700 hover:text-indigo-600 disabled:opacity-50"
                          >
                            {togglingId === company.id
                              ? 'Updating...'
                              : company.active
                                ? 'Deactivate'
                                : 'Activate'}
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {meta && meta.last_page > 0 && (
              <div className="px-6 py-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <span>Rows per page</span>
                  <select
                    value={perPage}
                    onChange={(e) => {
                      setPerPage(Number(e.target.value));
                      setPage(1);
                    }}
                    className="px-2 py-1 border border-slate-200 rounded text-sm bg-white"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  <span>
                    {(meta.current_page - 1) * meta.per_page + 1}–
                    {Math.min(meta.current_page * meta.per_page, meta.total)} of {meta.total}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={meta.current_page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <span className="text-sm text-slate-600">
                    Page {meta.current_page} of {meta.last_page}
                  </span>
                  <button
                    type="button"
                    disabled={meta.current_page >= meta.last_page}
                    onClick={() => setPage((p) => p + 1)}
                    className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">New Company</h2>
                <p className="text-sm text-slate-500">Creates company, admin user, and default setup</p>
              </div>
              <button type="button" onClick={closeCreateModal} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleCreate} className="p-6 space-y-6">
              {createError && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{createError}</span>
                </div>
              )}

              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Company details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                    <input
                      type="text"
                      required
                      value={createForm.name}
                      onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                    {createFieldError('name') && (
                      <p className="mt-1 text-sm text-red-600">{createFieldError('name')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={createForm.email ?? ''}
                      onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                    <input
                      type="text"
                      value={createForm.phone ?? ''}
                      onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Currency</label>
                    <input
                      type="text"
                      maxLength={3}
                      value={createForm.currency ?? 'LKR'}
                      onChange={(e) =>
                        setCreateForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))
                      }
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Tax number</label>
                    <input
                      type="text"
                      value={createForm.tax_number ?? ''}
                      onChange={(e) => setCreateForm((f) => ({ ...f, tax_number: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                    <textarea
                      rows={2}
                      value={createForm.address ?? ''}
                      onChange={(e) => setCreateForm((f) => ({ ...f, address: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-slate-900 mb-3">Tenant admin user</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Admin name *</label>
                    <input
                      type="text"
                      required
                      value={createForm.admin.name}
                      onChange={(e) =>
                        setCreateForm((f) => ({
                          ...f,
                          admin: { ...f.admin, name: e.target.value },
                        }))
                      }
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                    {createFieldError('admin.name') && (
                      <p className="mt-1 text-sm text-red-600">{createFieldError('admin.name')}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Admin email *</label>
                    <input
                      type="email"
                      required
                      value={createForm.admin.email}
                      onChange={(e) =>
                        setCreateForm((f) => ({
                          ...f,
                          admin: { ...f.admin, email: e.target.value },
                        }))
                      }
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                    {createFieldError('admin.email') && (
                      <p className="mt-1 text-sm text-red-600">{createFieldError('admin.email')}</p>
                    )}
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Admin password *</label>
                    <input
                      type="password"
                      required
                      minLength={8}
                      value={createForm.admin.password}
                      onChange={(e) =>
                        setCreateForm((f) => ({
                          ...f,
                          admin: { ...f.admin, password: e.target.value },
                        }))
                      }
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                    />
                    {createFieldError('admin.password') && (
                      <p className="mt-1 text-sm text-red-600">{createFieldError('admin.password')}</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeCreateModal}
                  className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-4 py-2 text-sm font-semibold text-white bg-indigo-700 hover:bg-indigo-600 rounded-lg disabled:opacity-50"
                >
                  {creating ? 'Creating...' : 'Create Company'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
